import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/kfmApi";
import { Loader2, ArrowRight, BookOpen } from "lucide-react";

function Paragraphs({ text, className }) {
  const parts = (text || "").split(/\n{2,}/).filter((p) => p.trim());
  return (
    <>
      {parts.map((p, i) => (
        <p key={i} className={className}>{p.trim()}</p>
      ))}
    </>
  );
}

function TopBar() {
  return (
    <header className="sticky top-0 z-20 bg-[#07090E]/85 backdrop-blur-md border-b border-[#1E273D]">
      <div className="max-w-3xl mx-auto px-6 h-16 flex items-center justify-between">
        <Link to="/" data-testid="shared-section-logo" className="flex items-center gap-2">
          <img src="/openword-logo.png" alt="" className="w-6 h-6" />
          <span className="text-lg text-white">OpenWord AI</span>
        </Link>
        <Link
          to="/login"
          data-testid="shared-section-signin"
          className="text-sm font-medium text-[#07090E] bg-[#00E5FF] hover:bg-[#00B8CC] rounded-full px-5 h-9 flex items-center transition-colors"
        >
          Read along
        </Link>
      </div>
    </header>
  );
}

function SignupCta({ testid, reference }) {
  return (
    <section
      data-testid={testid}
      className="rounded-2xl bg-[#10222A] border border-[#00E5FF]/15 p-6 sm:p-8 text-center space-y-4"
    >
      <div className="flex items-center justify-center gap-2 text-xs font-medium uppercase tracking-widest text-[#00E5FF]">
        <BookOpen className="w-4 h-4" strokeWidth={1.5} /> Study the whole passage
      </div>
      <p className="text-xl text-slate-200 leading-relaxed">
        This is one part of a study {reference ? `on ${reference}` : ""}. OpenWord AI is a
        Scripture-first virtual study companion for personal Bible study — free to begin.
      </p>
      <Link
        to="/login"
        data-testid={`${testid}-btn`}
        className="inline-flex items-center gap-2 rounded-full bg-[#00E5FF] hover:bg-[#00B8CC] text-[#07090E] px-7 h-12 text-base font-medium transition-colors"
      >
        Study it for yourself <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
      </Link>
      <p className="text-xs text-slate-500">No cost — and OpenWord AI is a study tool; the Word itself remains the belief system.</p>
    </section>
  );
}

export default function SharedSection() {
  const { token } = useParams();
  const [data, setData] = useState(undefined); // undefined = loading, null = unavailable
  const pinged = useRef(false);

  useEffect(() => {
    api
      .get(`/section-share/${token}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null));
    if (!pinged.current) {
      pinged.current = true;
      api.post(`/section-share/${token}/view`).catch(() => {});
    }
  }, [token]);

  if (data === undefined) {
    return (
      <div className="min-h-screen bg-[#07090E] flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-[#00E5FF] animate-spin" strokeWidth={1.5} />
      </div>
    );
  }

  if (data === null) {
    return (
      <div className="min-h-screen bg-[#07090E]">
        <TopBar />
        <div className="max-w-lg mx-auto px-6 py-24 text-center space-y-6">
          <h1 className="text-3xl text-white">This section isn&apos;t available.</h1>
          <p className="text-slate-400">The link may have been removed by its author.</p>
          <SignupCta testid="shared-section-cta-error" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#07090E]">
      <TopBar />
      <article data-testid="shared-section" className="max-w-3xl mx-auto px-6 py-10 sm:py-16 space-y-8">
        <div className="space-y-2">
          {data.passage_reference && (
            <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-widest text-[#00E5FF]">
              <BookOpen className="w-4 h-4" strokeWidth={1.5} />
              {data.passage_reference}
              {data.translation_name ? ` · ${data.translation_name}` : ""}
            </div>
          )}
          <h1 data-testid="shared-section-heading" className="text-3xl sm:text-4xl text-white leading-tight">
            {data.heading || data.study_title || "A section from a study"}
          </h1>
          {data.study_title && data.heading && (
            <p className="text-sm text-slate-400">From the study &ldquo;{data.study_title}&rdquo;</p>
          )}
          {data.shared_by && <p className="text-sm text-slate-500">Shared by {data.shared_by}</p>}
        </div>

        <div className="text-lg leading-relaxed text-slate-300 space-y-4">
          <Paragraphs text={data.commentary} />
        </div>

        <SignupCta testid="shared-section-cta" reference={data.passage_reference} />
      </article>
    </div>
  );
}
